import { useState } from "react";
import { CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const TERMS = [
  ["Square always charges its card processing fee", "on every donation. At checkout, donors can choose to add the fee to their donation so your school receives the full amount; if they don't, your school receives the donation minus Square's fee."],
  ["Donations are deposited directly into this Square account.", "PagePledge never holds the money."],
  ["PagePledge doesn't take a percentage of donations.", "The only per-donation fee is Square's."],
  ["PagePledge doesn't issue charitable tax receipts.", "Donors get an emailed receipt for their records, not an official tax receipt."],
];

/**
 * What connecting Square means for the school's money, and the Connect
 * Square button, which stays disabled until the admin ticks that they
 * agree. The link carries agreed=1, which the authorize action requires;
 * the time rides through OAuth and lands on
 * payment_accounts.connection_terms_accepted_at. Used by the setup guide
 * and Settings. `children` sit beside the button (e.g. an "Open a Square
 * account" link).
 */
export default function SquareConnectAgreement({ href, size = "lg", children }) {
  const [agreed, setAgreed] = useState(false);
  const button = (
    <>
      <CreditCard className="h-4 w-4" />
      Connect Square
    </>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="font-medium text-foreground">Before you connect</p>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground marker:text-border">
          {TERMS.map(([lead, rest]) => (
            <li key={lead}><strong className="font-medium text-foreground">{lead}</strong> {rest}</li>
          ))}
        </ul>
        <label className="mt-4 flex cursor-pointer items-start gap-3 border-t border-border pt-4 text-sm">
          <Checkbox className="mt-0.5" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span className="leading-snug text-foreground">I understand how fees, deposits and receipts work, and I agree.</span>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {agreed ? (
          <Button size={size} asChild>
            {/* Plain <a> — the server redirects to Square's site */}
            <a href={`${href}?agreed=1`}>{button}</a>
          </Button>
        ) : (
          <Button size={size} disabled>{button}</Button>
        )}
        {children}
      </div>
    </div>
  );
}
